import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Award } from "lucide-react";

/**
 * /players/:playerId — Public career-stats Player Profile.
 * Reads /api/public/player/:playerId (no auth). Live refresh on match events.
 */
const STAGE_LABEL = { group: "League", quarter: "QF", semi: "SF", final: "Final" };
const fmtDate = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return isNaN(d) ? "TBD" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function PlayerProfile() {
  // Support both :id (legacy) and :playerId (new) URL params
  const params = useParams();
  const playerId = params.playerId || params.id;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get(`/public/player/${playerId}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Player not found");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId]);

  useEffect(() => {
    const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
    const es = new EventSource(`${base}/api/public/events/stream`);
    const refresh = () => load();
    es.addEventListener("match.set_scored", refresh);
    es.addEventListener("match.finalized", refresh);
    es.onerror = () => { };
    return () => es.close();
    // eslint-disable-next-line
  }, [playerId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (tab === "upcoming") return data.matches.filter((m) => (m.status || "").toLowerCase() === "scheduled");
    if (tab === "live") return data.matches.filter((m) => (m.status || "").toLowerCase() === "live");
    if (tab === "finished") return data.matches.filter((m) => (m.status || "").toLowerCase() === "finished");
    return data.matches;
  }, [data, tab]);

  if (loading) return <div className="p-10 text-zinc-400" data-testid="player-profile-loading">Loading player profile…</div>;
  if (err) return <div className="p-10 text-red-400" data-testid="player-profile-error">{err}</div>;
  if (!data) return null;

  const { player, team, stats } = data;
  const brand = team?.brand_color || "#00A86B";

  return (
    <div className="min-h-screen bg-[#060d09] text-white pb-16" data-testid={`player-profile-${player.id}`}>
      <div className="relative border-b border-white/10 p-6 md:p-10" style={{ background: `linear-gradient(135deg, ${brand}22 0%, #0a1611 60%)` }}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white mb-4" data-testid="player-profile-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <a
          href={`${(api.defaults.baseURL || "").replace(/\/$/, "")}/players/${player.id}/poster.png`}
          download={`${(player.name || "player").replace(/\s+/g, "_")}_poster.png`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded border border-[#F4B400]/50 text-[#F4B400] hover:bg-[#F4B400]/10"
          data-testid="download-poster-btn"
        >
          <Award className="w-3.5 h-3.5" /> Download Poster
        </a>
        <div className="flex items-start gap-4 md:gap-6 flex-wrap">
          <div className="w-28 h-28 md:w-40 md:h-40 rounded-2xl bg-black/40 border border-white/10 overflow-hidden shrink-0">
            {player.photo_url
              ? <img src={player.photo_url} alt={player.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-4xl text-zinc-600">{(player.name || "?").slice(0, 1)}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Mara Padel League · Player Profile</div>
            <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tighter leading-none mt-1" data-testid="player-profile-name">
              {player.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
              {player.category && <Badge className="bg-[#F4B400] text-black uppercase">Cat {player.category}</Badge>}
              {player.seed_level && <Badge className="bg-white/10 border-white/10 text-white uppercase">Seed {player.seed_level}</Badge>}
              {player.is_international && <Badge className="bg-red-500/20 text-red-200 border-red-500/40 uppercase">Intl</Badge>}
              {player.current_ranking && <span>Rank <b className="text-white">#{player.current_ranking}</b></span>}
              {player.fip_ranking && <span>FIP <b className="text-white">#{player.fip_ranking}</b></span>}
            </div>
            {team && (
              <div className="mt-3">
                <Link to={`/teams/${team.id}`} className="inline-flex items-center gap-2 text-xs text-zinc-300 hover:text-[#F4B400]" data-testid="player-profile-team-link">
                  <div className="w-6 h-6 rounded bg-black/40 overflow-hidden">
                    {team.logo_url && <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />}
                  </div>
                  <span>Franchise · <b className="text-white">{team.name}</b>{team.group ? ` · Group ${team.group}` : ""}</span>
                </Link>
              </div>
            )}
            {player.bio && <p className="mt-3 text-sm text-zinc-300 max-w-2xl">{player.bio}</p>}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-2">
          <Chip label="Played" value={stats.matches_played} />
          <Chip label="Won" value={stats.won} tone="green" />
          <Chip label="Lost" value={stats.lost} tone="red" />
          <Chip label="Games Won" value={stats.games_won} tone="green" />
          <Chip label="Games Lost" value={stats.games_lost} tone="red" />
          <Chip label="Game Diff" value={`${stats.game_diff > 0 ? "+" : ""}${stats.game_diff}`} tone={stats.game_diff > 0 ? "green" : stats.game_diff < 0 ? "red" : "neutral"} />
        </div>
      </div>

      <div className="p-6 md:p-10">
        <Card className="p-5 bg-[#0f1a15] border-white/10">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Award className="w-4 h-4 text-[#F4B400]" />
            <h2 className="font-anton text-xl uppercase tracking-tight">Match Appearances</h2>
            <div className="ml-auto flex gap-1" data-testid="player-profile-tabs">
              {[["all", "All"], ["live", "Live"], ["upcoming", "Upcoming"], ["finished", "Completed"]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)}
                  data-testid={`player-tab-${k}`}
                  className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${
                    tab === k ? "bg-[#00A86B]/20 border-[#00A86B]/60 text-[#00A86B]"
                              : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
                  }`}>{label}</button>
              ))}
            </div>
          </div>
          {filtered.length === 0
            ? <div className="text-sm text-zinc-500 italic py-6 text-center">No matches in this view.</div>
            : (
              <div className="space-y-2">
                {filtered.slice(0, 60).map((m) => <MatchRow key={m.id} m={m} playerId={player.id} />)}
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}

function Chip({ label, value, tone = "neutral" }) {
  const colors = {
    green: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-400 border-red-500/30 bg-red-500/5",
    neutral: "text-white border-white/10 bg-black/30",
  };
  return (
    <div className={`p-2 rounded-lg border ${colors[tone]}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function MatchRow({ m, playerId }) {
  const status = (m.status || "").toLowerCase();
  const gA = m.total_games_won_a ?? m.score_a ?? 0;
  const gB = m.total_games_won_b ?? m.score_b ?? 0;
  const onA = ["1", "2", "3"].some((s) => ((m.seed_lineup_a || {})[s] || []).includes(playerId));
  return (
    <Link
      to={`/matches/${m.id}`}
      data-testid={`player-match-${m.id}`}
      className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10 bg-black/20 hover:border-[#F4B400]/40 hover:bg-black/30 transition-colors"
    >
      <div className="w-7 h-7 rounded bg-black/40 overflow-hidden shrink-0">
        {m.team_a?.logo_url && <img src={m.team_a.logo_url} alt="" className="w-full h-full object-contain" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white truncate">
          <span className={onA ? "text-[#F4B400]" : ""}>{m.team_a?.name || "TBD"}</span>
          <span className="text-zinc-500 mx-1">vs</span>
          <span className={!onA ? "text-[#F4B400]" : ""}>{m.team_b?.name || "TBD"}</span>
        </div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-2 flex-wrap">
          <span>{STAGE_LABEL[m.stage] || m.stage}</span>
          {m.seed && <span>· Seed {m.seed}</span>}
          {m.court && <span>· {m.court}</span>}
          {m.scheduled_at && <span>· {fmtDate(m.scheduled_at)}</span>}
        </div>
      </div>
      <span className={`text-[9px] uppercase px-2 py-0.5 rounded border ${status === "live" ? "bg-red-500/20 text-red-300 border-red-500/40" : status === "finished" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-zinc-500/20 text-zinc-300 border-zinc-500/40"}`}>{status}</span>
      <div className="font-anton text-base text-white min-w-[52px] text-right">{gA}<span className="text-zinc-500 mx-0.5">–</span>{gB}</div>
    </Link>
  );
}
